/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ErrorBox,
  ErrorMessage,
  ErrorTitle,
  FormCancelButton,
  FormDialogTitle,
  getFieldProps,
  getFormProps,
  getSubmitButtonProps,
  useApiClient,
  useForm,
} from '@marageti/z4-lib';
import { LoadingButton } from '@mui/lab';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSnackbar } from 'notistack';
import { TripMatchStatus } from '@marageti/z4-sdk/lib/travel';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';

const schema = z.object({
  comments: z.string().min(1, 'Please add a comment'),
});

// Label prefix for the stored status reason. The cancellation alerts decode `reason` by splitting on
// the first ":" and only display the text after it, so the comment must be prefixed with a label
// (matching the internal app's MatchSoldCanceled label). Without it, any ":" in the partner's comment
// would be treated as the delimiter and the leading part of the comment would be dropped.
const CANCELLATION_REASON_LABEL = 'Without Commission Refund';

type CancelSoldTripFormData = z.infer<typeof schema>;

type Props = {
  open: boolean;
  closeDialog: () => void;
  tripMatchId?: string;
  onSuccess?: () => void;
};

const CancelSoldTripFormDialog = ({
  open,
  closeDialog,
  tripMatchId,
  onSuccess,
}: Props) => {
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const [submissionError, setSubmissionError] = useState<ApiError | undefined>(undefined);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
  } = useForm<CancelSoldTripFormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { comments: '' },
  });

  const handleClose = () => {
    if (isSubmitting) return;
    // The Dialog unmounts its content on close (no keepMounted), so the form resets on its own —
    // we only clear the local submission error and close, matching the other form dialogs.
    setSubmissionError(undefined);
    closeDialog();
  };

  const onSubmit = async (data: CancelSoldTripFormData) => {
    if (!tripMatchId) return;
    try {
      await apiClient.tripMatchesClient.putTripMatchStatus(tripMatchId, {
        id: tripMatchId,
        status: TripMatchStatus.MatchSoldCanceled,
        statusInfo: {
          reason: `${CANCELLATION_REASON_LABEL}: ${data.comments}`,
        },
      });
      enqueueSnackbar('Trip canceled.', { variant: 'success' });
      // Close the dialog before firing side effects, so a re-render from the parent's refetch
      // (onSuccess) can never leave the modal open.
      reset();
      closeDialog();
      if (onSuccess) onSuccess();
    } catch (e) {
      setSubmissionError(e as ApiError);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          mx: 'auto',
        },
      }}
    >
      <FormDialogTitle handleClose={isSubmitting ? () => {} : handleClose}>
        Cancel Sold Trip
      </FormDialogTitle>
      <DialogContent>
        <form name="cancel-sold-trip" {...getFormProps(handleSubmit(onSubmit))}>
          <Stack spacing={3}>
            <Typography color="primary.light">
              By submitting this cancellation, you acknowledge that
              {' '}
              <Typography component="span" fontWeight="bold">you will not receive a refund of Zicasso&apos;s commission</Typography>
              , as outlined in the
              {' '}
              <MuiLink
                href="https://www.zicasso.com/tc-terms-agreement"
                target="_blank"
                rel="noopener noreferrer"
              >
                Travel Company Agreement
              </MuiLink>
              {' '}
              (section 5.1.5).
            </Typography>
            <Typography color="primary.light">
              If you believe this cancellation requires review, please contact
              {' '}
              <MuiLink href="mailto:partner-support@zicasso.com">partner-support@zicasso.com</MuiLink>
              {' '}
              before proceeding.
            </Typography>
            <TextField
              id="comments"
              label="Comments"
              required
              placeholder="Describe the reason for canceling this trip"
              multiline
              rows={5}
              {...register('comments')}
              {...getFieldProps('comments', errors)}
            />
            <Typography color="error" fontWeight="bold">
              This action cannot be undone.
            </Typography>
            {submissionError && (
              <ErrorBox>
                <ErrorTitle>
                  Submission Error
                </ErrorTitle>
                <ErrorMessage>
                  {submissionError.message}
                </ErrorMessage>
              </ErrorBox>
            )}
          </Stack>
          <Divider sx={{ mx: '-32px', mt: '40px' }} />
          <Box sx={{ py: 3 }}>
            <Stack
              direction={{ mobile: 'column', desktop: 'row' }}
              justifyContent="flex-start"
              spacing={2.5}
              width="100%"
            >
              <LoadingButton
                type="submit"
                variant="contained"
                color="error"
                sx={{ width: { mobile: '100%', desktop: 'fit-content' } }}
                {...getSubmitButtonProps(isSubmitting, isValid)}
              >
                Confirm
              </LoadingButton>
              <FormCancelButton onClick={handleClose} />
            </Stack>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CancelSoldTripFormDialog;
